import {ApiError, Checkout} from "@connox/typescript-library";
import {ICartData} from "@connox/typescript-library/types/cart/CartData";
import braintree, {BraintreeError, PayPalCheckout, PayPalCheckoutCreatePaymentOptions} from "braintree-web";
import React from "react";
import * as Sentry from "@sentry/browser";
import {getShopId} from "../getShopId";
import {IPaymentModule, IPurchaseButtonProps, ISubmitPaymentResponse} from "./IPaymentModule";
import {PaymentController, isoLocales, INTERNAL_ERROR_TYPE} from "./PaymentController";
import {
  AuthorizationData,
  AuthorizationTokenizePayload,
  Button,
  ButtonColorOption,
  ButtonLabelOption,
  ButtonShapeOption,
  ButtonSizeOption,
  Environment,
  FlowType,
  Intent,
  LineItem,
  LineItemKind,
} from "paypal-checkout-components";
import {IPaymentTransaction} from "@connox/typescript-library/types/checkout/requestHandlers/CheckoutRequestHandler";
import window from "../WindowExtended";

export default class PaypalModule implements IPaymentModule {
  private readonly checkout: Checkout;
  private readonly paypalLocale: string;
  private paymentTransactionResponse?: IPaymentTransaction;
  private instance?: PayPalCheckout;
  private tokenizePayload?: AuthorizationTokenizePayload;
  private handleError?: (
    error: BraintreeError | ApiError | Error,
    occurence: string,
    additional?: Record<string, unknown>,
  ) => void;

  constructor(checkout: Checkout) {
    this.checkout = checkout;
    this.paypalLocale = isoLocales[getShopId()];
    this.PurchaseButton = this.PurchaseButton.bind(this) as (props: IPurchaseButtonProps) => React.ReactElement;
    this.onEnterSummary = this.onEnterSummary.bind(this) as () => Promise<void>;
  }

  public async onSubmitPayment(): Promise<ISubmitPaymentResponse> {
    const {client} = await PaymentController.initBraintreeClient();
    this.instance = await braintree.paypalCheckout.create({client});

    return Promise.resolve({success: true});
  }

  public async onEnterSummary(): Promise<void> {
    const paymentObject = this.buildPaymentDataObject();

    // This is only happening, when delivery address is not set
    if (!paymentObject) {
      throw Error("Shipping line1 format is invalid.");
    }
    if (!this.instance) {
      await this.onSubmitPayment();
    }

    // AFTER POPUP CLOSING
    // Errors will go to paypal onError method
    // const onAuthorize = async (data, action) => {
    const onAuthorize = async (data: AuthorizationData) => {
      if (!this.instance) throw new Error("No paypal checkout instance in onAutorize");
      this.tokenizePayload = await this.instance.tokenizePayment(data);
      if (window.annotationTextArea !== (this.checkout.customerRemark ?? undefined)) {
        await this.checkout.put({customerRemark: window.annotationTextArea});
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }
      // Only needed for error handling
      this.paymentTransactionResponse = await PaymentController.initPayment();
      await new Promise((resolve) => setTimeout(resolve, 1000));
      this.paymentTransactionResponse = await PaymentController.authorizePayment(this.tokenizePayload.nonce);
      await new Promise((resolve) => setTimeout(resolve, 1000));
      await this.checkout.purchase();

      return this.tokenizePayload;
    };

    // BEFORE OPENING POPUP
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    const payment = (): Promise<string> => {
      if (!this.instance) throw new Error("No paypal checkout instance in 'payment' method");

      return this.instance.createPayment(paymentObject);
    };

    return import("paypal-checkout").then((paypal: IPaypal) =>
      paypal.Button.render(
        {
          commit: true,
          env: (__DEVELOPMENT__ ? "sandbox" : "production") as Environment,
          locale: this.paypalLocale,
          onAuthorize,
          onError: (error) => {
            // Don't ask why, but `error` is a string in braintree paypal type
            // definitions and an Error instance in production environment
            const tempError = typeof error === "string" ? new Error(error) : (error as unknown as Error);

            // Check if there will ever be a case, where handleError is not set
            // just temporary and only relevant for Sentry.
            if (!this.handleError) {
              tempError.name = INTERNAL_ERROR_TYPE.PAYPAL.OnErrorNoHandle + tempError.name;
              Sentry.captureException(tempError, {
                extra: {
                  occurence: "PayPal Purchase Button, no handleError",
                },
              });

              return;
            }

            tempError.name = INTERNAL_ERROR_TYPE.PAYPAL.OnError + tempError.name;
            this.handleError(tempError, "PayPal Purchase Button");
          },
          payment,
          style: {
            ...payPalButtonStyle,
            label: "buynow" as ButtonLabelOption,
            tagline: true,
          },
        },
        "#PurchaseNow",
      ),
    );
  }

  public PurchaseButton(props: IPurchaseButtonProps): React.ReactElement {
    this.handleError = props.handleError;

    const purchaseNowDiv = document.querySelector("#PurchaseNow");
    if (purchaseNowDiv && purchaseNowDiv.innerHTML.length) {
      purchaseNowDiv.innerHTML = "";
    }

    return React.createElement("div", {id: "PurchaseNow"}, null);
  }

  public async onPaymentSelect(): Promise<void> {
    return Promise.resolve();
  }

  public isDataComplete(): boolean {
    return true;
  }

  private buildPaymentDataObject = (): PayPalCheckoutCreatePaymentOptions | null => {
    const {cartData, deliveryAddress} = this.checkout;

    if (!deliveryAddress) {
      return null;
    }

    const {city, countryCode, firstName, lastName, state, street, zip} = deliveryAddress;
    const streetnumber = deliveryAddress.number;

    return {
      amount: cartData.value.payable.amount,
      currency: cartData.currencyCode,
      displayName: "connox - be unique",
      flow: "checkout" as FlowType,
      intent: "authorize" as Intent,
      lineItems: getLineItems(cartData),
      locale: this.paypalLocale,
      shippingAddressEditable: false,
      shippingAddressOverride: {
        city,
        countryCode,
        line1: ["GB", "FR"].includes(countryCode) ? `${streetnumber} ${street}` : `${street} ${streetnumber}`,
        postalCode: zip,
        recipientName: `${firstName} ${lastName}`,
        state: state ?? "",
      },
    };
  };
}

export interface IPaypal {
  Button: typeof Button;
}

export const payPalButtonStyle = {
  color: "gold" as ButtonColorOption,
  height: 40,
  label: "paypal" as ButtonLabelOption,
  shape: "rect" as ButtonShapeOption,
  size: "responsive" as ButtonSizeOption,
  tagline: false,
  branding: true,
};

export function getLineItems(cartData: ICartData): LineItem[] {
  const itemPositions = cartData.positions.map<LineItem>((position) => {
    const {item, price, quantity, text} = position;
    const numberPriceAmount = Number(price.amount);
    const tempObject = {
      kind: (quantity < 0 !== numberPriceAmount < 0 ? "credit" : "debit") as LineItemKind,
      name: text,
      quantity: Math.abs(quantity).toString(),
      unitAmount: Math.abs(numberPriceAmount).toString(),
      unitTaxAmount: undefined,
      description: item?.title,
      productCode: undefined,
      url: undefined,
    };

    if (position.type === "Item" && item) {
      return {...tempObject, productCode: item.itemId.toString(), url: item.url};
    }

    return tempObject;
  });

  const rebatePositions = cartData.rebates.map<LineItem>((rebate) => {
    return {
      kind: "credit" as LineItemKind,
      name: rebate.code,
      quantity: "1",
      unitAmount: rebate.amount.amount,
      description: undefined,
      unitTaxAmount: undefined,
      productCode: undefined,
      url: undefined,
    };
  });

  return [...itemPositions, ...rebatePositions];
}
